import { Button } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDecodedParams } from "../misc/utils";
import { useAws } from "../aws";
import styles from "./ConfirmationPage.module.scss";

const ConfirmationPage = () => {
  const { t } = useTranslation();
  const { type } = useDecodedParams();
  const navigate = useNavigate();
  const { awsHttp } = useAws();
  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState({});

  const {
    paymentMethod,
    donationAmount,
    prefix,
    chineseLastname,
    chineseFirstname,
    englishLastname,
    englishFirstame,
    gender,
    confirmEmail,
    phoneType,
    phoneNumber,
    birthYear,
    birthMonth,
    receiptName,
    receiptAddress,
    job,
    livingArea,
    option,
    skuName,
    files,
    frequency,
    programName,
  } = data;


  const isMonthly = frequency === "monthly";

  const parseStore = (key) => {
    try {
      return JSON.parse(window.sessionStorage.getItem(key)) || {};
    } catch (e) {
      return {};
    }
  };

  useEffect(() => {
    setData((prev) => {
      // Never return null/undefined — the top-level destructure of `data` would throw.
      if (!type) {
        navigate(-1);
        return prev;
      }
      if (type === "donation") return parseStore("donation");
      if (type === "event") return parseStore("event");
      return prev;
    });
  }, [type]);

  const buildDetail = (d) => ({
    birth_month: d.birthMonth,
    contact_email: d.confirmEmail || d.email,
    contact_phone: d.phoneNumber,
    gender: d.gender,
    name: {
      prefix: d.prefix,
      en_first_name: d.englishFirstame,
      en_last_name: d.englishLastname,
      zh_first_name: d.chineseFirstname,
      zh_last_name: d.chineseLastname,
    },
  });

  const startMonthlySubscription = () => {
    if (submitting) return;
    setSubmitting(true);
    const origin = window.location.origin;
    awsHttp({
      HTTPRequestMethod: "POST",
      APIFunction: "user/create_subscription",
      unauthorize: true,
      requestBody: {
        amount: Number(donationAmount),
        // Monthly donations are always 一般用途 (no programme selector on that tab). Forced
        // here too, because the form state is shared: a programme picked on the one-off
        // tab must not leak into the subscription.
        program_id: "ANY",
        success_origin: origin,
        success_url: `${origin}/donation/2430e09`,
        cancel_url: `${origin}/confirmation/donation`,
        detail: buildDetail(data),
        promo_terms: data["pi-1"] ? "yes" : "no",
        // Tax-receipt details; stored top-level on the record so the CMS export
        // and the receipt PDF read them the same way as legacy donations.
        receipt: data.receiptName ? "YES" : "NO",
        receipt_name: data.receiptName || "",
        receipt_address: data.receiptAddress || "",
      },
    })
      .then((res) => {
        const body = res?.data?.body;
        if (res?.data?.statusCode === 200 && body?.checkout_url) {
          window.sessionStorage.setItem(
            "recurring",
            JSON.stringify({ manage_token: body.manage_token, d_id: body.d_id, programName: "一般用途", amount: donationAmount })
          );
          window.location.href = body.checkout_url;
        } else {
          setSubmitting(false);
          alert(typeof body === "string" ? body : "設立每月捐款時發生錯誤，請重試。");
        }
      })
      .catch(() => {
        setSubmitting(false);
        alert("設立每月捐款時發生錯誤，請重試。");
      });
  };

  const startOneOffCheckout = () => {
    if (submitting) return;
    setSubmitting(true);
    const origin = window.location.origin;
    awsHttp({
      HTTPRequestMethod: "POST",
      APIFunction: "user/create_donation_checkout",
      unauthorize: true,
      requestBody: {
        amount: Number(donationAmount),
        program_id: data.program || "ANY",
        success_origin: origin,
        success_url: `${origin}/donation/2430e09`,
        cancel_url: `${origin}/confirmation/donation`,
        detail: buildDetail(data),
        promo_terms: data["pi-1"] ? "yes" : "no",
        receipt: data.receiptName ? "YES" : "NO",
        receipt_name: data.receiptName || "",
        receipt_address: data.receiptAddress || "",
      },
    })
      .then((res) => {
        const body = res?.data?.body;
        if (res?.data?.statusCode === 200 && body?.checkout_url) {
          // Stash the record id so the success page can put it on the receipt.
          window.sessionStorage.setItem("lastOneOff", JSON.stringify({ d_id: body.d_id, amount: donationAmount, programName }));
          window.location.href = body.checkout_url;
        } else {
          setSubmitting(false);
          alert(typeof body === "string" ? body : "處理捐款時發生錯誤，請重試。");
        }
      })
      .catch(() => {
        setSubmitting(false);
        alert("處理捐款時發生錯誤，請重試。");
      });
  };

  const handleSubmit = () => {
    if (type === "donation") {
      if (isMonthly) {
        // Monthly recurring: create the Stripe subscription (test mode) and
        // redirect to hosted Checkout.
        startMonthlySubscription();
      } else if (paymentMethod === "Card") {
        // One-off card: same hosted Stripe Checkout as monthly (no raw PAN
        // through our API). FPS/PayMe still use the QR flow.
        startOneOffCheckout();
      } else {
        navigate("/payment/donation");
      }
    } else if (type === "event") {
      navigate("/payment/event");
    }
  };

  return (
    <div className="page-view event-detail">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={submitting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <img className="logo mt-2 mb-6" src="/logoPNB.png" alt="pause-and-breathe-logo" />
      <form>
        <div className={`card-view donation-card donation-result-card`} style={{ marginBottom: "40px" }}>
          <h1 className="mt-2 mb-1">{t("Confirmation")}</h1>
          <h4 className="">{type === "donation" ? t("Donation") : t("Event Enrollment")}</h4>
          {type === "donation" ? (
            <>
              {/* donation type + program + combined summary line */}
              <div className={styles["item-container"]}>
                <TextItem label="捐款類型" value={isMonthly ? "每月定期捐款" : "單次捐款"} />
                {!isMonthly && <TextItem label="捐款支持計劃" value={programName || "一般用途"} />}
              </div>
              <div className={styles["item-container"]}>
                <TextItem label={isMonthly ? "每月捐款金額" : "捐款金額"} value={isMonthly ? `${donationAmount}／月` : donationAmount} />
              </div>
              {isMonthly ? (
                <div
                  style={{
                    background: "#eaf4fb",
                    border: "1px solid #bfddf1",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    margin: "4px 0 16px",
                  }}
                >
                  <h5 style={{ fontWeight: 600 }}>
                    {`每月定期捐款 港幣$${Number(donationAmount || 0).toLocaleString()}／月`}
                  </h5>
                  <p style={{ fontSize: "12px", color: "#555", marginTop: "6px" }}>
                    確認後，將於每月同日自動從您的信用卡扣款；每次扣款成功後，捐款收據會自動發送至您的電郵。您可隨時取消每月捐款。
                  </p>
                </div>
              ) : null}
              {receiptName ? (
                <>
                  <div className={styles["item-container"]}>
                    <TextItem label="免稅收據抬頭名稱" value={receiptName} />
                  </div>
                  <div className={styles["item-container"]}>
                    <TextItem label="收據郵奇地址" value={receiptAddress} />
                  </div>
                </>
              ) : null}
              <div className={styles["divider"]} />
              <div className={styles["item-container"]}>
                <TextItem label="稱謂" value={prefix} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="中文姓氏" value={chineseLastname} />
                <TextItem label="中文名字" value={chineseFirstname} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="英文姓氏" value={englishLastname} />
                <TextItem label="英文姓氏" value={englishFirstame} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="性別" value={gender} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="電郵" value={confirmEmail} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="聯絡電話類型" value={phoneType} />
                <TextItem label="電話" value={phoneNumber} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="出生月份" value={birthMonth} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="接收推廣資訊" value={data["pi-1"] ? "是" : "否"} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="付款方法" value={paymentMethod} />
              </div>
            </>
          ) : (
            <>
              <div className={styles["item-container"]}>
                <TextItem label="選項" value={`${skuName} - ${option?.description}`} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="價格" value={option?.price} />
              </div>
              <div className={styles["divider"]} />
              <div className={styles["item-container"]}>
                <TextItem label="稱謂" value={prefix} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="中文姓氏" value={chineseLastname} />
                <TextItem label="中文名字" value={chineseFirstname} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="英文姓氏" value={englishLastname} />
                <TextItem label="英文姓氏" value={englishFirstame} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="性別" value={gender} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="電郵" value={confirmEmail} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="聯絡電話類型" value={phoneType} />
                <TextItem label="電話" value={phoneNumber} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="出生年份" value={birthYear} />
                <TextItem label="出生月份" value={birthMonth} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="職業" value={job} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="居住地區" value={livingArea} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="接收推廣資訊" value={data["pi-0"] ? "是" : "否"} />
                <TextItem label="同意拍攝及錄影" value={data["pi-1"] ? "是" : "否"} />
              </div>
              <div className={styles["item-container"]}>
                <TextItem label="付款方法" value={paymentMethod} />
              </div>
              {files?.length ? (
                <>
                  <div className={styles["divider"]} />
                  <h5 style={{ marginBottom: "8px" }}>{"補充資料"}</h5>
                  {files?.length
                    ? files.map((file, i) => {
                      return <FileItem value={file.data.filename} />;
                    })
                    : null}
                </>
              ) : null}
            </>
          )}
        </div>
        <div className={`my-5 btns-gp-div`}>
          <Button
            sx={{ marginRight: 2 }}
            onClick={() => {
              navigate(-1);
            }}
          >
            {t("Back")}
          </Button>
          <Button variant="contained" sx={{ marginRight: 2 }} onClick={handleSubmit} disabled={submitting}>
            {isMonthly || paymentMethod === "Card" ? "確認並前往付款" : t("Submit")}
          </Button>
        </div>
      </form>
    </div>
  );
};

const TextItem = ({ label, value }) => {
  return (
    <div className={styles["text-item"]}>
      <h5>{label || ""}</h5>
      <p>{value || "N/A"}</p>
    </div>
  );
};

const FileItem = ({ label, value }) => {
  return (
    <div className={styles["file-item"]}>
      <p>{value || "N/A"}</p>
    </div>
  );
};

export default ConfirmationPage;
